import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Box, type SystemStyleObject } from "@hightouch/htui";
import merge from "lodash/merge";
import type { AnyExtension, JSONContent } from "@tiptap/core";
import {
  EditorContent,
  useEditor,
  useEditorState,
  type Editor,
} from "@tiptap/react";

import { useTextareaChrome } from "src/hooks/use-textarea-chrome";

import {
  buildMarkdownEditorExtensions,
  type MarkdownEditorExtensionOptions,
} from "./extensions";
import {
  outsideReportBlocks,
  REPORT_BLOCK_SELECTOR,
} from "./report-block-scope";
import { ReportBlockView } from "./report-block-view";
import { MarkdownToolbar } from "./toolbar";

/**
 * What the editor opens with: markdown, or a document markdown can't express.
 * Read once on mount; uncontrolled after that, so remount (new `key`) to load
 * different content. Either way, edits emit markdown.
 */
type InitialContent =
  | { initialValue: string; initialDocument?: never }
  | { initialDocument: JSONContent; initialValue?: never };

export type MarkdownEditorProps = InitialContent & {
  /** Accessible name for the editable Markdown document. */
  "aria-label"?: string;
  /** Called with serialized markdown whenever the user edits the document. */
  onChange: (markdown: string) => void;
  isReadOnly?: boolean;
  placeholder?: string;
  /**
   * Feature-specific extensions appended to the base schema (e.g. the context
   * hub's citation embeds and slash menu). Read once on mount, like
   * `initialValue` — the first render's value wins and later changes are
   * ignored, so extensions must reach live data through refs, not props.
   */
  extraExtensions?: AnyExtension[];
  /** Styles merged over the editor surface, for feature-specific nodes. */
  editorSx?: SystemStyleObject;
  /** Called with the editor once it exists, and with null when it goes away. */
  onEditorReady?: (editor: Editor | null) => void;
  /** Extra link schemes to accept. Read once on mount, like `extraExtensions`. */
  linkProtocols?: MarkdownEditorExtensionOptions["linkProtocols"];
  /**
   * Feature-specific controls rendered at the end of the toolbar, after a
   * divider. A plain React render, so unlike `extraExtensions` it reads live
   * props on every render.
   */
  toolbarActions?: (editor: Editor) => ReactNode;
  /**
   * Pins the toolbar to the top of the nearest scrolling ancestor. For an
   * editor that grows with its content inside a page that scrolls.
   */
  isToolbarSticky?: boolean;
  /** How far below its scroller's top edge a sticky toolbar pins, on the spacing scale. */
  stickyToolbarOffset?: number;
  /**
   * Drops the editor's own frame so it reads as the page it sits on. The
   * document grows with its content and the toolbar pins to the scroller.
   */
  isFrameless?: boolean;
  /**
   * A bare field for short prose inside a page: no toolbar, frame, padding or
   * minimum height. Markdown shortcuts (`## `, `- `, `**bold**`) still apply.
   */
  isInline?: boolean;
  /** Like `isInline`, but framed and padded as the design-system textarea, for a form field. */
  isField?: boolean;
  /** Leaves undo and redo out of the toolbar, for a host that draws them elsewhere. */
  hideHistory?: boolean;
};

const EDITOR_SX: SystemStyleObject = {
  // Inherited from the original markdown-editor spike (#40778), extended in
  // the same idiom for the wider schema (h3–h6, blockquote, hr, inline code,
  // tables).
  // Report blocks keep the report renderer's styles, so the surface's element rules stop at them.
  ".ht-md-prosemirror": outsideReportBlocks({
    outline: "none",
    minHeight: "240px",
    padding: 4,
    fontSize: "md",
    lineHeight: "1.7",
    color: "text.primary",
    "> * + *": { marginTop: 3 },
    "h1, h2, h3, h4, h5, h6": {
      fontWeight: "semibold",
      lineHeight: "1.3",
      // The app-wide `strong` weight (500) is lighter than a heading, and semibold is the heaviest cut.
      strong: { fontWeight: "inherit" },
    },
    h1: { fontSize: "2xl" },
    h2: { fontSize: "xl" },
    h3: { fontSize: "lg" },
    "ul, ol": { paddingLeft: 6 },
    ul: { listStyleType: "disc" },
    ol: { listStyleType: "decimal" },
    "li > p": { margin: 0 },
    a: { color: "primary.base", textDecoration: "underline" },
    blockquote: {
      borderLeft: "3px solid",
      borderColor: "base.border",
      paddingLeft: 3,
      color: "text.secondary",
    },
    hr: {
      border: "none",
      borderTop: "1px solid",
      borderColor: "base.border",
    },
    code: {
      fontFamily: "mono",
      fontSize: "sm",
      backgroundColor: "base.background",
      borderRadius: "sm",
      paddingX: 1,
    },
    pre: {
      fontFamily: "mono",
      fontSize: "sm",
      backgroundColor: "base.background",
      borderRadius: "md",
      padding: 3,
      whiteSpace: "pre-wrap",
      code: { backgroundColor: "transparent", padding: 0 },
    },
    // ProseMirror marks the node view's outer element selected when a report block is clicked or arrowed onto.
    [`.ProseMirror-selectednode > ${REPORT_BLOCK_SELECTOR}`]: {
      borderRadius: "md",
      outline: "2px solid",
      outlineColor: "primary.base",
      outlineOffset: 2,
    },
    "pre[data-raw-markdown]": {
      borderLeft: "3px solid",
      borderColor: "base.border",
      backgroundColor: "base.lightBackground",
      color: "text.secondary",
    },
    ".tableWrapper": { overflowX: "auto" },
    table: {
      borderCollapse: "collapse",
      width: "100%",
      // Fixed layout stops columns from re-widthing per keystroke (the
      // realignment jitter); paired with the cell wrapping below, a single
      // long value wraps instead of growing the table into endless scroll.
      tableLayout: "fixed",
      "th, td": {
        border: "1px solid",
        borderColor: "base.border",
        padding: 2,
        verticalAlign: "top",
        position: "relative",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        "> p": { margin: 0 },
      },
      th: {
        backgroundColor: "base.lightBackground",
        fontWeight: "semibold",
        textAlign: "left",
        // As in headings, the app-wide `strong` weight (500) would lighten semibold text.
        strong: { fontWeight: "inherit" },
      },
      // Tiptap marks cell-range selections with this class.
      ".selectedCell": { backgroundColor: "primary.background" },
    },
  }),
  // Popup rendered by the slash-menu extension (./slash-menu.ts). Styled here
  // because the extension appends plain DOM inside the editor wrapper.
  ".ht-slash-menu": {
    position: "absolute",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overscrollBehavior: "contain",
    backgroundColor: "white",
    border: "1px solid",
    borderColor: "base.border",
    borderRadius: "md",
    boxShadow: "md",
    padding: 1,
  },
  ".ht-slash-item": {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 0.5,
    padding: 2,
    borderRadius: "sm",
    textAlign: "left",
    cursor: "pointer",
    "&[data-selected]": { backgroundColor: "base.lightBackground" },
    "&:hover": { backgroundColor: "base.lightBackground" },
  },
  ".ht-slash-label": { fontWeight: "medium", fontSize: "sm" },
  ".ht-slash-hint": { fontSize: "xs", color: "text.tertiary" },
};

const INLINE_SX: SystemStyleObject = {
  // The extra `&` outranks the base surface rule instead of replacing it.
  "& .ht-md-prosemirror": { padding: 0, minHeight: "auto" },
};

// ProseMirror's injected base CSS, which a static render doesn't get unless an editor has mounted.
const STATIC_SX: SystemStyleObject = {
  ".ht-md-prosemirror": outsideReportBlocks({
    position: "relative",
    overflowWrap: "break-word",
    whiteSpace: "break-spaces",
    fontVariantLigatures: "none",
    fontFeatureSettings: '"liga" 0',
    // The editor keeps an empty paragraph one line tall with a trailing <br>.
    "p:empty::before": { content: '"\\200b"' },
  }),
};

const STATIC_EDITOR_SX = merge({}, EDITOR_SX, STATIC_SX);

/**
 * Read-only HTML from `markdownToHtml`, styled like the editor's surface. For
 * views that show a doc but never edit it, where a live editor per instance is
 * too costly.
 */
export const MarkdownStaticHtml = ({ html }: { html: string }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [reportBlocks, setReportBlocks] = useState(NO_ELEMENTS);
  // biome-ignore lint/correctness/useExhaustiveDependencies: new html replaces the placeholder elements, so they are found again
  useLayoutEffect(() => {
    const found = contentRef.current?.querySelectorAll<HTMLElement>(
      REPORT_BLOCK_SELECTOR,
    );
    // A thumbnail is a picture of the doc, so its blocks' buttons, links and toggles stay out of the tab order.
    for (const element of found ?? []) element.setAttribute("inert", "");
    setReportBlocks(found?.length ? Array.from(found) : NO_ELEMENTS);
  }, [html]);
  return (
    <Box sx={STATIC_EDITOR_SX}>
      <Box
        ref={contentRef}
        className="ht-md-prosemirror"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: serialized by ProseMirror from the editor schema, so text is escaped and links pass the link allow-list
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {reportBlocks.map((element, index) =>
        createPortal(
          <ReportBlockView source={element.dataset.source ?? ""} isThumbnail />,
          element,
          String(index),
        ),
      )}
    </Box>
  );
};

// One empty array, so a doc without report blocks doesn't re-render.
const NO_ELEMENTS: HTMLElement[] = [];

type Inset = number | string;

const PlaceholderOverlay = ({
  editor,
  placeholder,
  insetX,
  insetY,
}: {
  editor: Editor;
  placeholder: string;
  insetX: Inset;
  insetY: Inset;
}) => {
  const isEmpty = useEditorState({
    editor,
    selector: ({ editor: instance }) => instance.isEmpty,
  });
  if (!isEmpty) return null;
  return (
    <Box
      position="absolute"
      top={insetY}
      left={insetX}
      color="text.placeholder"
      pointerEvents="none"
      fontSize="md"
    >
      {placeholder}
    </Box>
  );
};

/**
 * Google-Docs-style WYSIWYG markdown editor. Markdown is the single source of
 * truth: `initialValue` is parsed into the editor on mount (unless a caller
 * opens an `initialDocument` instead), and every user edit serializes back to
 * markdown via `onChange`. ProseMirror/Tiptap JSON is in-memory editing state
 * only and is never surfaced to callers.
 *
 * Uncontrolled, per Tiptap's own model: the editor owns the document after
 * mount and `initialValue` is never synced back in. To load different
 * content, remount with a new `key` (the React idiom for resetting
 * uncontrolled state) — an in-place setContent replace maps the selection
 * and undo bookmarks to the doc end and is exactly the bug class this
 * design avoids.
 *
 * No-edit ⇒ no rewrite: `onChange` fires only from user editing transactions
 * (Tiptap's `onUpdate`); mounting and unmounting never emit, so opening a doc
 * and closing it cannot rewrite (normalize) it.
 */
export const MarkdownEditor = ({
  "aria-label": ariaLabel,
  initialValue,
  initialDocument,
  onChange,
  isReadOnly = false,
  placeholder,
  extraExtensions,
  editorSx,
  linkProtocols,
  toolbarActions,
  isToolbarSticky = false,
  stickyToolbarOffset = 0,
  isFrameless: isFramelessProp = false,
  isInline = false,
  isField = false,
  hideHistory = false,
  onEditorReady,
}: MarkdownEditorProps) => {
  const isFrameless = isFramelessProp || isInline;
  const hasFrame = !isFrameless && !isField;
  const { dsStyle: textareaStyle, containerSx: textareaSx } =
    useTextareaChrome();
  // Stable across the editor's lifetime.
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once on mount, like initialValue
  const extensions = useMemo(
    () => [
      ...buildMarkdownEditorExtensions({ linkProtocols }),
      ...(extraExtensions ?? []),
    ],
    [],
  );

  const editor = useEditor(
    {
      extensions,
      ...(initialDocument
        ? { content: initialDocument, contentType: "json" }
        : { content: initialValue, contentType: "markdown" }),
      editable: !isReadOnly,
      editorProps: {
        attributes: {
          class: "ht-md-prosemirror",
          role: "textbox",
          "aria-multiline": "true",
          ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
        },
      },
      onUpdate: ({ editor: instance }) => onChange(instance.getMarkdown()),
    },
    [],
  );

  useEffect(() => {
    // emitUpdate: false — v3 setEditable fires the update event by default,
    // which would push serializer-normalized markdown through onChange on
    // mount and falsely dirty the form.
    editor?.setEditable(!isReadOnly, false);
  }, [editor, isReadOnly]);

  // Read through a ref so a caller's new callback each render doesn't re-announce the editor.
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;
  useEffect(() => {
    if (!editor) return;
    onEditorReadyRef.current?.(editor);
    return () => onEditorReadyRef.current?.(null);
  }, [editor]);

  const hasToolbar = Boolean(editor) && !isReadOnly && !isInline && !isField;
  // A sticky toolbar keeps its rounded corners while pinned, so in that mode
  // the toolbar and the document each draw their part of the frame; the
  // frame itself would clip the toolbar inside its own straight sides.
  const isSplitFrame = isToolbarSticky && hasToolbar && !isFrameless;
  // Deep merge: a caller's override of one `.ht-md-prosemirror` property must
  // not drop the base heading, list and spacing rules under the same key.
  const sx = useMemo(
    () =>
      merge(
        {},
        EDITOR_SX,
        isInline ? INLINE_SX : {},
        isField
          ? {
              ...textareaSx,
              // Padding on the surface, not the frame, so a click anywhere in the field places the cursor.
              "& .ht-md-prosemirror": {
                px: textareaStyle.px,
                py: textareaStyle.py,
                minHeight: textareaStyle.minHeight,
              },
            }
          : {},
        editorSx,
      ),
    [isInline, isField, textareaSx, textareaStyle, editorSx],
  );

  return (
    // In a height-bounded flex parent the editor fills it (flex 1) and the
    // document area scrolls under the pinned toolbar; in an unbounded context
    // flex-basis auto lets it grow with content like a normal block.
    <Box
      sx={sx}
      border={isSplitFrame || !hasFrame ? undefined : "1px solid"}
      borderColor={hasFrame ? "base.border" : undefined}
      borderRadius={hasFrame ? "3xl" : undefined}
      backgroundColor={hasFrame ? "white" : undefined}
      // Visible lets the toolbar stick to the page scroll; hidden would trap it.
      overflow={isSplitFrame || isFrameless ? "visible" : "hidden"}
      display="flex"
      flexDirection="column"
      flex={1}
      minHeight={0}
    >
      {editor &&
        hasToolbar &&
        (isFrameless ? (
          <Box
            position="sticky"
            top={stickyToolbarOffset}
            zIndex={1}
            bg="white"
          >
            <MarkdownToolbar
              editor={editor}
              toolbarActions={toolbarActions}
              hideHistory={hideHistory}
            />
          </Box>
        ) : isSplitFrame ? (
          <StickyToolbar offset={stickyToolbarOffset}>
            <MarkdownToolbar
              editor={editor}
              toolbarActions={toolbarActions}
              hideHistory={hideHistory}
            />
          </StickyToolbar>
        ) : (
          <MarkdownToolbar
            editor={editor}
            toolbarActions={toolbarActions}
            hideHistory={hideHistory}
          />
        ))}
      <Box
        position="relative"
        flex={1}
        minHeight={0}
        overflowY={isFrameless ? undefined : "auto"}
        border={isSplitFrame ? "1px solid" : undefined}
        borderTop={isSplitFrame ? "none" : undefined}
        borderColor="base.border"
        borderBottomRadius={isSplitFrame ? "3xl" : undefined}
        display="flex"
        flexDirection="column"
        // Stretch EditorContent (and the ProseMirror surface) to the pane
        // bottom so clicking below a short doc still focuses the editor.
        sx={{
          "> div:last-of-type": {
            display: "flex",
            flexDirection: "column",
            flex: "1 0 auto",
          },
          ".ht-md-prosemirror": { flex: "1 0 auto" },
        }}
      >
        {/* An empty read-only doc is just empty — a "write here" prompt next
            to a non-editable surface would be a lie. */}
        {editor && placeholder && !isReadOnly && (
          <PlaceholderOverlay
            editor={editor}
            placeholder={placeholder}
            insetX={isField ? (textareaStyle.px as Inset) : isInline ? 0 : 4}
            insetY={isField ? (textareaStyle.py as Inset) : isInline ? 0 : 4}
          />
        )}
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

// The toolbar's corner radius, which the corner mask below it covers.
const STICKY_TOOLBAR_RADIUS = "var(--chakra-radii-3xl)";

/**
 * Pins the toolbar, with the frame's rounded top, while the document scrolls
 * under it.
 */
function StickyToolbar({
  offset,
  children,
}: {
  offset: number;
  children: ReactNode;
}) {
  return (
    <>
      {/* Pinned with the toolbar, under its rounded corners, so the document's side borders don't show past the curve. */}
      <Box
        position="sticky"
        top={offset}
        zIndex={1}
        height={STICKY_TOOLBAR_RADIUS}
        mb={`calc(-1 * ${STICKY_TOOLBAR_RADIUS})`}
        bg="white"
        aria-hidden
      />
      <Box
        position="sticky"
        top={offset}
        zIndex={1}
        bg="white"
        border="1px solid"
        borderBottom="none"
        borderColor="base.border"
        borderTopRadius="3xl"
        overflow="hidden"
      >
        {children}
      </Box>
    </>
  );
}
